// libraries
// styles
import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useGetVirtualAccounts } from 'src/api/query/creditcards'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import ExpensePolicyUploadBox from 'src/components/ExpensePolicyUploadBox/ExpensePolicyUploadBox'
import LayoutShell from 'src/components/LayoutShell/LayoutShell'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import OrgMemberDetailsBox from 'src/components/OrgMemberDetailsBox/OrgMemberDetailsBox'
import OrgNameAndIndustryBox from 'src/components/OrgNameAndIndustryBox/OrgNameAndIndustryBox'
import { SubnavDesign } from 'src/components/Subnav/Subnav'
import OrgVirtualCardsEnablePanel from 'src/feature/OrgVirtualCardsEnablePanel/OrgVirtualCardsEnablePanel'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import {
  useHasSaasFeatures,
  useQueryOrganizationSaasTier,
} from 'src/queries/organizations/saas.queries'
import { getOrganization } from 'src/redux/actions/data/organizations.actions'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectOrganizationIdFromRoute,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectExpensePolicyAIWhitelistFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

import styles from './OrganizationInfo.style'

const OrganizationInfo: React.FC = () => {
  const user = useSelector(selectUserListModelFromState)
  const organizationId = useSelector(selectOrganizationIdFromRoute)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const appName = useSelector(selectAppName)

  const hasSaasFeatures = useHasSaasFeatures()
  const { show_expense_policy_ai: showExpensePolicyAIFF } = useSelector(
    selectFeatureFlaggerFromState,
  )
  const expensePolicyAIWhitelist = useSelector(
    selectExpensePolicyAIWhitelistFromRemoteConfigState,
  )
  const showExpensePolicyAI =
    showExpensePolicyAIFF ||
    expensePolicyAIWhitelist?.includes(user?.organizationId || '')

  const translate = useTranslate()

  const dispatch = useBackgroundDispatch()
  useEffect(() => {
    if (organizationId) {
      dispatch(getOrganization(organizationId, true))
    }
    // TODO - should not assume user exists in redux, so should load user here if iti doesn't exist
    // dispatch(getUser)
  }, [organizationId])

  useQueryOrganizationSaasTier({
    orgId: organization?.id || '',
    enabled: !!organization?.id,
  })

  const { data: virtualAccountsData } = useGetVirtualAccounts(undefined, {
    enabled: !!organization?.id,
  })
  const virtualAccounts = virtualAccountsData?.virtualAccounts ?? []
  // TODO: Multiple virtual accounts not yet supported - using first account
  const virtualAccount =
    virtualAccounts.length > 0 ? virtualAccounts[0] : undefined

  if (user === undefined || organization === undefined) {
    return <LoadingBackgroundPage />
  }

  return (
    <>
      <Helmet>
        <title>{`Organization Info | ${appName}`}</title>
        <meta
          name="description"
          content={`Organization Info page of ${appName}`}
        />
      </Helmet>
      <LayoutShell
        subnavOptions={[]}
        subnavDesign={SubnavDesign.Floating}
        content={
          <BackgroundPageContent
            className="OrganizationInfo"
            css={styles}
            noGap
          >
            <OrgNameAndIndustryBox user={user} organization={organization} />
            <OrgMemberDetailsBox user={user} organization={organization} />
            {virtualAccount && (
              <OrgVirtualCardsEnablePanel virtualAccount={virtualAccount} />
            )}
            {showExpensePolicyAI && hasSaasFeatures && (
              <ExpensePolicyUploadBox organization={organization} user={user} />
            )}
          </BackgroundPageContent>
        }
        title={<span>{translate('Organization Info')}</span>}
        buttons={[]}
      />
    </>
  )
}

export default OrganizationInfo
